
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Building2, Package, ShoppingCart, Users, BarChart3, Shield, Zap, Globe, Database, Lock, TrendingUp, CheckCircle2, Star, Award, Boxes, CreditCard, FileText, Settings, Cpu, Cloud } from 'lucide-react'
import { useState, useEffect } from 'react'

export default function LandingPage() {
  const navigate = useNavigate()
  const [bgLoaded, setBgLoaded] = useState(false)

  useEffect(() => {
    const img = new Image()
    img.src = '/auth-bg.png'
    img.onload = () => setBgLoaded(true)
    img.onerror = () => setBgLoaded(true)
  }, [])

  const stats = [
    { value: '99.9%', label: 'Uptime SLA', icon: Shield },
    { value: '24/7', label: 'Global Support', icon: Globe },
    { value: 'ISO 27001', label: 'Certified', icon: Award },
    { value: 'Fortune', label: 'Grade Security', icon: Lock },
  ]

  const features = [
    {
      icon: Building2,
      title: 'Core Operations',
      description: 'Multi-store management with centralized company control',
      items: ['Company Management', 'Multi-Store Operations', 'Cash Register Control', 'Store Hierarchy'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Package,
      title: 'Inventory Excellence',
      description: 'Advanced product management with variant support',
      items: ['Product Management', 'Real-time Stock Tracking', 'Variant & SKU Support', 'Category Hierarchy'],
      color: 'from-green-500 to-green-600'
    },
    {
      icon: ShoppingCart,
      title: 'Order Processing',
      description: 'Complete order lifecycle management',
      items: ['Order Management', 'Multi-Payment Support', 'Table Service', 'Kitchen Integration'],
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Users,
      title: 'Customer Engagement',
      description: 'Comprehensive CRM and loyalty programs',
      items: ['Customer Profiles', 'Loyalty Programs', 'Gift Cards', 'Customer Segmentation'],
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: FileText,
      title: 'Fiscal Compliance',
      description: 'Enterprise-grade compliance and reporting',
      items: ['Fiscal Documents', 'Tax Management', 'Audit Trail', 'Regulatory Compliance'],
      color: 'from-red-500 to-red-600'
    },
    {
      icon: BarChart3,
      title: 'Business Intelligence',
      description: 'Advanced analytics and reporting tools',
      items: ['Custom Reports', 'Real-time Analytics', 'KPI Tracking', 'Multi-location Insights'],
      color: 'from-indigo-500 to-indigo-600'
    },
  ]

  const techStack = [
    { name: 'PostgreSQL', description: 'Enterprise Database', icon: Database },
    { name: 'Supabase', description: 'Real-time Backend', icon: Cloud },
    { name: 'Multi-DB Support', description: 'PostgreSQL, MySQL, MSSQL, SQLite', icon: Boxes },
    { name: 'Offline-First', description: 'Works Without Internet', icon: Zap },
  ]

  const capabilities = [
    'Fortune 500 Security Standards',
    'Real-time Synchronization',
    'Multi-Currency Support',
    'Offline Mode Capable',
    'Plugin Architecture',
    'Workflow Automation',
    'Role-Based Access Control',
    'Comprehensive Audit Logs',
    'Multi-location Support',
    'Advanced Reporting Engine',
    'Loyalty & Gift Cards',
    'Restaurant Floor Plans',
  ]

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-no-repeat bg-fixed relative overflow-x-hidden"
      style={bgLoaded ? { backgroundImage: 'url(/auth-bg.png)', backgroundSize: 'cover', backgroundAttachment: 'fixed' } : { background: '#1a1a1a' }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900/70 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-4 py-20">
          <div className="max-w-7xl w-full">
            <div className="text-center space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-white/90 font-medium">Global Standard</span>
              </div>

              {/* Main Heading */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h1 className="text-7xl md:text-8xl lg:text-9xl font-medium text-white drop-shadow-2xl tracking-wide leading-relaxed">
                    ELYSIUM
                  </h1>
                  <div className="flex items-center justify-center gap-4">
                    <div className="h-px w-24 bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
                    <p className="text-2xl md:text-3xl lg:text-4xl text-blue-400 font-bold tracking-[0.3em] uppercase">
                      POS
                    </p>
                    <div className="h-px w-24 bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
                  </div>
                </div>
                
                <div className="space-y-3 max-w-5xl mx-auto">
                  <p className="text-xl md:text-2xl text-white/90 font-light tracking-wide">
                    Mission-Critical Point of Sale Infrastructure
                  </p>
                </div>
                
                <div className="h-0.5 w-48 bg-gradient-to-r from-transparent via-blue-500 to-transparent mx-auto"></div>
              </div>

              {/* Subtitle */}
              <div className="space-y-6 max-w-5xl mx-auto">
                <p className="text-xl md:text-2xl lg:text-3xl text-white font-medium leading-relaxed tracking-wide">
                  Single Source of Truth for Multi-National Retail Operations
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-white/80 text-sm md:text-base font-medium tracking-wide">
                  <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg">
                    <Shield className="w-5 h-5 text-blue-400" />
                    <span>Fortune 500 Certified</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg">
                    <Award className="w-5 h-5 text-blue-400" />
                    <span>ISO 27001 Compliant</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg">
                    <Lock className="w-5 h-5 text-blue-400" />
                    <span>Bank-Grade Security</span>
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="pt-12 space-y-8">
                <button
                  onClick={() => navigate('/login')}
                  className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-16 py-6 rounded-xl text-xl font-semibold tracking-wide transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 active:scale-95"
                >
                  Get Started
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </button>

                {/* Trust Indicators */}
                <div className="flex flex-wrap items-center justify-center gap-8 text-white/60 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span>No Credit Card Required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span>Free Trial Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span>Enterprise Support</span>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-20 max-w-5xl mx-auto">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-8 hover:from-white/15 hover:to-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  >
                    <stat.icon className="w-10 h-10 text-blue-400 mb-4 mx-auto" />
                    <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-sm text-white/70 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-4">
                Comprehensive Features
              </h2>
              <p className="text-xl text-white/70 max-w-2xl mx-auto">
                Everything you need to run a world-class retail or restaurant operation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
                >
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-white/70 mb-6">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-white/80">
                        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Stack */}
        <section className="py-20 px-4 bg-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-4">
                Enterprise Technology Stack
              </h2>
              <p className="text-xl text-white/70 max-w-2xl mx-auto">
                Built on proven, scalable technologies trusted by Fortune 500 companies
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {techStack.map((tech, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105 text-center"
                >
                  <tech.icon className="w-12 h-12 text-blue-400 mb-4 mx-auto" />
                  <h3 className="text-xl font-bold text-white mb-2">{tech.name}</h3>
                  <p className="text-white/70 text-sm">{tech.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-4">
                Enterprise Capabilities
              </h2>
              <p className="text-xl text-white/70 max-w-2xl mx-auto">
                Industry-leading features designed for mission-critical operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <Star className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  <span className="text-white/90">{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border border-white/20 rounded-3xl p-12 text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Join thousands of businesses running on enterprise-grade POS infrastructure
              </p>
              <button
                onClick={() => navigate('/login')}
                className="group inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-12 py-5 rounded-xl text-xl font-semibold tracking-wide transition-all duration-200 shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 active:scale-95"
              >
                Get Started Now
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Elysium POS</h3>
                <p className="text-white/70">
                  Fortune 500 standard point of sale system built for enterprise operations.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Resources</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Documentation</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Support</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">API Reference</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Privacy Policy</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">Company</h4>
                <ul className="space-y-2">
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">About Us</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Contact</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Careers</a></li>
                  <li><a href="#" className="text-white/70 hover:text-white transition-colors">Terms of Service</a></li>
                </ul>
              </div>
            </div>
            <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                © 2024 Elysium POS. All rights reserved. • Enterprise Edition v1.0.0
              </p>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <Shield className="w-4 h-4" />
                <span>ISO 27001 Certified • Fortune 500 Standards • Bank-Grade Security</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
